import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import SavePaymentMethodInline from "./SavePaymentMethodInline";
import Select from 'react-select';

// 1) Load Stripe with your public key
const isLive = !(window.location.hostname.includes('stage') || window.location.hostname.includes('local'))
const stripePromise = loadStripe(isLive ? "pk_live_51HaNWTFTZstRS4WcgEj195NFq0vhkZfc79rYpxhpuWxPD31NGM7j8gRChTJb65wziOuyMEGgFMPsKkfmDruGT8Q000keA5Xgde": "pk_test_51HGJFvARqlCjj4b98dGKPv0wmGuBnhd1UlEELmE0B7LWglLJi03i6ieQDjZ3IOu8Hd5oshnSTaUsUPRlLVcBwvUT00mnga5GLc");



const payrollSofwareOptions = {
  "UK": [
      {label: "BrightPay", value:"BrightPay"},
      {label: "Employment Hero", value:"Employment Hero"},
      {label: "Moneysoft: Payroll Manager", value:"Moneysoft: Payroll Manager"},
      {label: "Iris", value:"Iris"},
      {label: "Sage", value:"Sage"},
      {label: "Xero", value:"Xero"},
      {label: "Quickbooks", value:"Quickbooks"},
      {label: "FreeAgent", value:"FreeAgent"},
      {label: "Other", value:"Other"}
  ],
  "MT": [
      {label: "Shireburn Indigo Payroll", value:"Shireburn Indigo Payroll"},
      {label: "Dakar", value:"Dakar"},
      {label: "Salaroo", value:"Salaroo"},
      {label: "Excel", value:"Excel"},
      {label: "Talexio", value:"Talexio"},
      {label: "DataByte Payroll", value:"DataByte Payroll"},
      {label: "Other", value:"Other"},
  ]
}
const noOfEmployeesOptions = {
  MT: [
      { value: '1-5', label: '1-5 Employees' },
      { value: '6-10', label: '6-10 Employees' },
      { value: '11-20', label: '11-20 Employees' },
      { value: '21-40', label: '21-40 Employees' },
      { value: '41-80', label: '41-80 Employees' },
      { value: '81-200', label: '81-200 Employees' },
      { value: '200+', label: '200+ Employees' }
      ],
  UK: [
        { value: '1-25', label: '1-25 Employees' },
        { value: '26-50', label: '26-50 Employees' },
        { value: '51-100', label: '51-100 Employees' },
        { value: '101-250', label: '101-250 Employees' },
        { value: '251-500', label: '251-500 Employees' },
        { value: '501-1000', label: '501-1000 Employees' },
        { value: '1001-10000', label: '1,001-10,000 Employees' },
        { value: '10001+', label: '10,000+ Employees' }
  ]
}

const noOfPayrollsOptions = [
    { value: '1-10', label: '1-10 Payrolls' },
    { value: '11-50', label: '11-50 Payrolls' },
    { value: '51-100', label: '51-100 Payrolls' },
    { value: '101-250', label: '101-250 Payrolls' },
    { value: '251-1000', label: '251-1000 Payrolls' },
    { value: '1,001+', label: '1,000+ Payrolls' }
]

const fields = [
    {
        name: 'givenName',
        label: 'First Name',
        type: 'text',
        required: true
    },
    {
        name: 'familyName',
        label: 'Last Name',
        type: 'text',
        required: true
    },
    {
        name: 'phone',
        label: 'Phone',
        type: 'tel',
        required: true
    },
    {
        name: 'email',
        label: 'Email',
        type: 'email',
        required: true
    },
    {
        name: 'password',
        label: 'Password',
        type: 'password',
        required: true
    },
    {
        name: 'organisation',
        label: 'Organisation',
        type: 'text',
        required: true
    },
    {
        name: 'vatNumber',
        label: 'VAT Number',
        type: 'text',
        required: false
    },
    {
        name: 'noOfPayrolls',
        label: 'No. of Payrolls',
        type: 'list',
        required: false
    },
    {
        name: 'noOfEmployees',
        label: 'No. of Employees',
        type: 'list',
        required: false
    },
    {
        name: 'currentSoftware',
        label: 'Current Software',
        type: 'list',
        isMulti: true,
        required: false
    }
]


// 2) Main Single-Page Component
export default function RegistrationAndPayment({
	jurisdiction = "UK",
    partner
}) {
  const [data, setData] = useState({
    givenName: "", // 'Jonathan',
    familyName: "", // 'Mifsud',
    phone: "", // '12345678',
    email: "", // `jonathan+${Math.round(Math.random()*10000)}@buddy.hr`,
    password: "", // 'Password!2',
    organisation: "", // 'Test01',
    vatNumber: "", // 'MT0111111',
    noOfPayrolls: '',
    noOfEmployees: '',
    currentSoftware: '',
  });

    useEffect(() => {
        setData((prev) => ({ ...prev, partner }));
    }, [partner]);

  // State for managing SetupIntent client secret
  const [clientSecret, setClientSecret] = useState(null);

  // Track loading & errors
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Update form data as user types
  const handleChange = (e) => {
    setData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const validateForm = () => {
      fields.forEach(field => {
            if (field.required && !data[field.name]) {
                setErrorMessage(`${field.label} is required`);
                return false;
            }
            if (field.validate && !field.validate(data[field.name])) {
                setErrorMessage(`${field.label} is invalid`);
                return false;
            }
      })
      return true
  }

  // Once the payment method is set up, we finalize user creation
  // or subscription on the backend
  const handlePaymentMethodSaved = async (paymentMethodId) => {
    try {
      setLoading(true);
      const response = await fetch('/api/complete-registration', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...data,
          paymentMethodId,
        }),
      });
      const data = await response.json();
      if (data.error) {
        throw new Error(data.error);
      }
      // Registration + Payment Method success
      alert('Registration Complete! You can now access your account.');
    } catch (err) {
      setErrorMessage(err.message);
    } finally {
      setLoading(false);
    }
  };

return (
    <div className="bg-white sm:max-w-md sm:w-full sm:mx-auto sm:rounded-lg sm:overflow-hidden">
      <div className="px-4 py-8 sm:px-10">
        <div className="mt-6">
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start space-x-2">
              {/* <img
                className="h-8 w-auto sm:h-10"
                src={BuddyFace}
                alt="Buddy HR Payroll Software Logo"
              /> */}
              <h2 className="text-lg font-semibold text-gray-900">
                Hi, I'm Buddy, your new payroll companion. Tell me about yourself:
              </h2>
            </div>

            <div className="grid grid-cols-2 gap-6">
                {
                    fields.map(field => (
                        <div>
                            <label htmlFor={field.name} className="sr-only">
                                {field.label}
                            </label>
                            <div className="rounded-md shadow-sm">
                                {
                                    field.type === 'list' ? (
                                        <Select
                                            options={field.name === 'noOfEmployees' ? noOfEmployeesOptions[jurisdiction] : field.name === 'noOfPayrolls' ? noOfPayrollsOptions : payrollSofwareOptions[jurisdiction]}
                                            onChange={(selectedOption) => {
                                                setData((prev) => ({ ...prev, [field.name]: selectedOption.value }));
                                            }}
                                            isMulti={field.isMulti}
                                            placeholder={field.label}
                                          styles={{
                                            control: (baseStyles, state) => ({
                                              ...baseStyles,
                                              fontSize: '14px',
                                            }),
                                            option: (styles, { data, isDisabled, isFocused, isSelected }) => {
                                              return {
                                                ...styles,
                                                fontSize: '14px',
                                              };
                                            },
                                          }}
                                        />
                                    ) : (

                                        <input
                                            value={data[field.name]}
                                            onChange={handleChange}
                                            name={field.name}
                                            id={field.name}
                                            placeholder={field.label}
                                            required
                                            type={field.type}
                                            className="appearance-none block w-full px-3 py-2 border
                                                     border-gray-300 rounded-md placeholder-gray-400
                                                     focus:outline-none focus:shadow-outline-blue
                                                     focus:border-blue-300 transition duration-150
                                                     ease-in-out sm:text-sm sm:leading-5"
                                        />
                                    )
                                }
                            </div>
                        </div>
                    ))
                }
            </div>

            <h2 className="text-lg font-semibold text-gray-900">
                Billing Information
            </h2>
            <p className="text-xs text-gray-500">
                We need your billing information to set up your account.
                No worries, you will not be charged until your trial ends.
            </p>
            {/* Payment Element (deferred) */}
              {/*data.email && data.name && data.surname && */}
            {(
              <Elements
                stripe={stripePromise}
                options={{
                    locale: "en",
                    mode: 'setup',
                    currency: jurisdiction === "MT" ? "eur" : "gbp",
                    fields: {
                        billingDetails: {
                            name: 'never',
                            email: 'never',
                        }
                    }
                }}
              >
                <SavePaymentMethodInline
                  onPaymentMethodSaved={handlePaymentMethodSaved}
                  data={data}
                  validateForm={validateForm}
                  fullName={`${data.name} ${data.surname}`}
                  email={data.email}
                />
              </Elements>
            )}

            {/* Display any errors */}
            {errorMessage && (
              <div className="rounded-md bg-red-50 p-4">
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}

            {/* Submit button if needed (e.g., to partially save user details) */}
            {/*<div>
              <span className="block w-full rounded-md shadow-sm">
                <button
                  type="submit"
                  className="w-full flex justify-center py-2 px-4 border
                             border-transparent text-sm font-medium
                             rounded-md text-white bg-orange-600
                             hover:bg-orange-500 focus:outline-none
                             focus:border-orange-700 focus:shadow-outline-orange
                             active:bg-orange-700 transition duration-150 ease-in-out"
                >
                  Save Details (Optional)
                </button>
              </span>
            </div>*/}
          </div>
        </div>
      </div>

      {/*<div className="px-4 py-6 bg-gray-50 border-t-2 border-gray-200 sm:px-10">
        <p className="text-xs leading-5 text-gray-500">{stepDescription}</p>
        {loading && <p className="text-sm text-gray-500">Processing...</p>}
      </div>*/}
    </div>
  );
}
